import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { initGoogleAnalytics, trackGaPageView } from './analytics/googleAnalytics';
import { CartProvider }                from './context/CartContext';
import { AuthProvider, useAuth }       from './context/AuthContext';
import { LanguageProvider }            from './context/LanguageContext';
import { CoreLangProvider, useCoreT }            from './context/CoreLangContext';
import { useRestaurantSlug }           from './hooks/useRestaurantSlug';
import { restaurantApi, platformApi, PLATFORM_TOKEN_KEY } from './api/apiClient';

// NOTE: The "/" route (platform homepage + restaurant landing page) is handled
// entirely by Express — it serves a pure HTML file before React ever loads.
// React only handles /menu, /cart, /item/:id, /admin, /business, etc.
import MenuPage                  from './pages/MenuPage';
import OrderTrackingPage         from './pages/OrderTrackingPage';
import ItemPage                  from './pages/ItemPage';
import CartPage                  from './pages/CartPage';
import AdminLoginPage            from './pages/AdminLoginPage';
import AdminDashboard            from './pages/AdminDashboard';
import BusinessAuthPage          from './pages/BusinessAuthPage';
import BusinessDashboard         from './pages/BusinessDashboard';
import PlatformAdminLoginPage    from './pages/PlatformAdminLoginPage';
import PlatformAdminDashboard    from './pages/PlatformAdminDashboard';
import NotFoundPage              from './pages/NotFoundPage';

const PLATFORM_PROFILE_KEY = 'nooshin_platform_profile';
function loadLocal(k) { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } }

// Set the browser-tab favicon at runtime (platform or restaurant owner-uploaded).
function setDocFavicon(url) {
  if (!url) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
  link.href = url;
}

// ── Shared screens ────────────────────────────────────────────────────────────
function LoadingScreen() {
  const T = useCoreT ? useCoreT() : null;
  return (
    <div style={{ minHeight:'100vh', background:'#0a0a0a', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'1.5rem', fontFamily:'DM Sans,sans-serif' }}>
      <div style={{ fontSize:'3rem', animation:'spin 1.2s linear infinite' }}>🍽️</div>
      <p style={{ color:'rgba(255,255,255,0.35)', fontSize:'0.9rem', letterSpacing:'0.08em' }}>{T?.loading || 'Loading…'}</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ErrorScreen({ message }) {
  const T = useCoreT ? useCoreT() : null;
  return (
    <div style={{ minHeight:'100vh', background:'#0a0a0a', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'1rem', fontFamily:'DM Sans,sans-serif', color:'#f5f0e8', padding:'2rem', textAlign:'center' }}>
      <div style={{ fontSize:'3rem' }}>⚠️</div>
      <h2 style={{ fontFamily:'Playfair Display,serif', color:'#fff', fontSize:'1.5rem' }}>{T?.serverError || 'Could not reach the server'}</h2>
      <p style={{ color:'rgba(255,255,255,0.4)', maxWidth:'420px', lineHeight:'1.7', fontSize:'0.9rem' }}>{message}</p>
      <button onClick={() => window.location.reload()} style={{ marginTop:'0.5rem', padding:'0.8rem 2rem', background:'#c8a96e', border:'none', borderRadius:'10px', color:'#fff', cursor:'pointer', fontFamily:'DM Sans,sans-serif', fontSize:'0.95rem', fontWeight:'700' }}>{T?.retry || 'Retry'}</button>
    </div>
  );
}

// ── Platform admin wrapper ────────────────────────────────────────────────────
function PlatformAdminWrapper() {
  const [platformAdmin, setPlatformAdmin] = useState(() => loadLocal(PLATFORM_PROFILE_KEY));
  const [settings,      setSettings]      = useState(null);

  useEffect(() => {
    if (!localStorage.getItem(PLATFORM_TOKEN_KEY)) { setPlatformAdmin(null); return; }
    platformApi.getMe()
      .then(r  => setPlatformAdmin(r.data))
      .catch(() => { localStorage.removeItem(PLATFORM_TOKEN_KEY); localStorage.removeItem(PLATFORM_PROFILE_KEY); setPlatformAdmin(null); });
  }, []);

  useEffect(() => {
    if (platformAdmin) platformApi.getSettings().then(r => setSettings(r.data)).catch(() => {});
  }, [platformAdmin]);

  const coreMode = settings?.coreMode || 'global';
  const coreLang = settings?.coreLang || settings?.defaultLanguage || '';

  if (!platformAdmin) {
    return (
      <CoreLangProvider coreMode={coreMode} coreLang={coreLang} coreTranslations={settings?.coreTranslations || {}}>
        <PlatformAdminLoginPage onLogin={setPlatformAdmin} />
      </CoreLangProvider>
    );
  }
  return (
    <CoreLangProvider coreMode={coreMode} coreLang={coreLang} coreTranslations={settings?.coreTranslations || {}}>
      <LanguageProvider platformSettings={settings}>
        <PlatformAdminDashboard admin={platformAdmin} onLogout={() => setPlatformAdmin(null)} settings={settings} onSettingsChange={setSettings} />
      </LanguageProvider>
    </CoreLangProvider>
  );
}

// ── Platform routes (bare domain) ─────────────────────────────────────────────
// "/" is served as HTML by Express — React never sees that route.
// React only handles /business/* and /platform-admin.
function PlatformRoutes({ platformSettings }) {
  const { businessOwner } = useAuth();
  return (
    <LanguageProvider platformSettings={platformSettings}>
      <Routes>
        <Route path="/business/login"     element={businessOwner ? <Navigate to="/business/dashboard" /> : <BusinessAuthPage />} />
        <Route path="/business/dashboard" element={businessOwner ? <BusinessDashboard /> : <Navigate to="/business/login" />} />
        <Route path="/platform-admin"     element={<PlatformAdminWrapper />} />
        <Route path="/platform-admin/*"   element={<PlatformAdminWrapper />} />
        {/* "/" is never reached here — Express intercepts it first */}
        <Route path="*"                   element={<Navigate to="/business/login" />} />
      </Routes>
    </LanguageProvider>
  );
}

// ── Restaurant routes (subdomain) ─────────────────────────────────────────────
// "/" is served as HTML by Express (with real restaurant data injected).
// React handles /menu, /cart, /item/:id, /admin, /business.
function RestaurantRoutes({ slug }) {
  const { adminUser, businessOwner } = useAuth();
  const [restaurant, setRestaurant]  = useState(null);
  const [loading,    setLoading]     = useState(true);
  const [error,      setError]       = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    restaurantApi.getBySlug(slug)
      .then(res => { if (!cancelled) setRestaurant(res.data); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  // Use the restaurant's favicon (or logo) for the browser tab on its subdomain.
  useEffect(() => { setDocFavicon(restaurant?.favicon || restaurant?.logo); }, [restaurant]);

  if (loading) return <LoadingScreen />;
  if (error)   return <ErrorScreen message={error} />;
  if (!restaurant) return <NotFoundPage slug={slug} />;

  return (
    <LanguageProvider restaurantSettings={restaurant}>
      <Routes>
        <Route path="/menu"     element={<MenuPage     restaurant={restaurant} setRestaurant={setRestaurant} />} />
        <Route path="/item/:id" element={<ItemPage     restaurant={restaurant} />} />
        <Route path="/cart"     element={<CartPage     restaurant={restaurant} />} />
        <Route path="/order/:orderId" element={<OrderTrackingPage restaurant={restaurant} />} />

        <Route path="/admin/login" element={
          adminUser?.restaurantSlug === restaurant.slug
            ? <Navigate to="/admin" />
            : <AdminLoginPage restaurant={restaurant} />
        } />
        <Route path="/admin" element={
          adminUser?.restaurantSlug === restaurant.slug
            ? <AdminDashboard restaurant={restaurant} setRestaurant={setRestaurant} />
            : <Navigate to="/admin/login" />
        } />

        <Route path="/business/login"     element={businessOwner ? <Navigate to="/business/dashboard" /> : <BusinessAuthPage />} />
        <Route path="/business/dashboard" element={businessOwner ? <BusinessDashboard /> : <Navigate to="/business/login" />} />

        {/* "/" is never reached — Express intercepts it first and serves HTML */}
        <Route path="*" element={<Navigate to="/menu" />} />
      </Routes>
    </LanguageProvider>
  );
}

// ── Root app ──────────────────────────────────────────────────────────────────
function AppRoutes() {
  const slug = useRestaurantSlug();
  const [platformSettings, setPlatformSettings] = useState(null);

  useEffect(() => {
    platformApi.getSettings()
      .then(r => setPlatformSettings(r.data))
      .catch(() => {});
  }, []);

  // Platform favicon for non-restaurant routes (homepage shell, business/admin).
  useEffect(() => {
    if (slug === null) setDocFavicon(platformSettings?.favicon || platformSettings?.logo);
  }, [slug, platformSettings]);

  const coreMode = platformSettings?.coreMode || 'global';

  return (
    <CoreLangProvider
      coreMode={coreMode}
      coreLang={platformSettings?.coreLang || platformSettings?.defaultLanguage || ''}
      coreTranslations={platformSettings?.coreTranslations || {}}
    >
      {slug === null
        ? <PlatformRoutes platformSettings={platformSettings} />
        : <RestaurantRoutes slug={slug} />}
    </CoreLangProvider>
  );
}

// Fires a Google Analytics page_view on every client-side route change. gtag is
// usually already loaded by the server-rendered head; when it isn't (client-only
// routes) initGoogleAnalytics() bootstraps it from REACT_APP_GA_ID if set.
function GaTracker() {
  const location = useLocation();
  useEffect(() => { initGoogleAnalytics(); }, []);
  useEffect(() => { trackGaPageView(location.pathname + location.search); }, [location.pathname, location.search]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <GaTracker />
      <AuthProvider>
        <CartProvider>
          <AppRoutes />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
